// multiPointCollector.js — Shared base "multi-point tool" for bespoke sequential-click draw
// interactions (ticket 04).
//
// Before this file, each of miscBespokeTools.js/directionalArrowTools.js/lineSpineWidthTools.js/
// lineArcSideTools.js/areaGeometricTools.js/areaPolygonTools.js/areaRadialTools.js/
// areaSpecialTools.js reimplemented its own click/pointermove/keydown wiring, its own
// cleanup(), and its own ad hoc vertex-dot styling — and the result was INCONSISTENT: only one
// file (areaPolygonTools's AREA10) supported Escape-to-cancel, and NONE supported
// Backspace-to-undo-last-point, before this ticket. Consolidating the mechanics here (not the
// bespoke arc/ring/bracket sketch rendering, which still varies too much per DrawRule family to
// usefully share) means every migrated tool gets the same conventions automatically, and a
// future new bespoke tool built on this base can't quietly drop one of them the way every
// existing tool but AREA10 already had. (fixedPointCollector.js is the one file in this
// ticket's scope that ISN'T built on this base — see that file's own comment for why: it's a
// single-click-and-spread gesture with no sequential state to share this module's mechanics
// with.)
//
// Follows the mapbox-gl-draw RadiusMode-extends-line-mode pattern (a small mode/behavior that
// composes shared primitives rather than reimplementing them) and tldraw's StateNode
// phase-hierarchy pattern (this module's finished/cancelled states are terminal — once
// finish()/cancel() fires, the collector tears itself down and further events are ignored).
//
// Auto-complete rule (ticket 04): for a DrawRule with min === max — a STRICTLY fixed point
// count — the collector calls onFinish automatically on the click that reaches that count, no
// confirm keystroke needed. For any DrawRule with min < max — a genuinely variable/optional
// point count (e.g. AXIS's N-waypoint spine, or a width point with a min/max range) — Enter or
// double-click is required once minCount is reached, UNLESS the click reaches maxCount (a
// finite cap), which still auto-completes (e.g. AREA10's min=3/max=6 already auto-committed at
// 6 before this ticket, and keeps doing so here).
//
// Does NOT decide each DrawRule's point order/semantics (P1=tip vs P1=rear, reversal, etc.) —
// callers apply that transform themselves, either before collected coords reach this module
// (not applicable here, since this module IS the collector) or by wrapping the `onFinish`
// callback they pass in. This module only ever hands back raw click-order [lng,lat] pairs.

import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import Feature from 'ol/Feature';
import { Point } from 'ol/geom';
import { toLonLat } from 'ol/proj';
import { PREVIEW_DOT_CURSOR, getRoleDotStyle } from './drawPreviewStyles';

/**
 * @param {import('ol/Map').default} map
 * @param {object} opts
 * @param {number} opts.minCount — minimum clicks before Enter/double-click/cap can finish.
 * @param {number} [opts.maxCount=Infinity] — click count that auto-completes (no confirm
 *   needed). Pass `minCount` itself for a strictly fixed-count DrawRule.
 * @param {(coords: [number,number][]) => void} opts.onFinish — called once, with the raw
 *   click-order [lng,lat] array, when the collector completes normally. NOT called on
 *   Escape-cancel or external destroy().
 * @param {(index: number) => string} [opts.roleForIndex] — returns the semantic role
 *   ('anchor'|'direction'|'width', see drawPreviewStyles.js's ROLE_DOT_STYLES) of the point at
 *   the given 0-based collected-index, for handle styling. Defaults to always 'anchor'.
 * @param {(ctx: { previewSource: import('ol/source/Vector').default, collected3857: Array,
 *   cursorCoord3857: [number,number]|null }) => void} [opts.onSketchUpdate] — called after the
 *   base's own per-point role-styled dots are (re)drawn into `previewSource`, so callers can
 *   add their own extra bespoke sketch features (connecting lines, arcs, rings, width
 *   brackets, …) onto the SAME layer/source without reimplementing dot rendering or having to
 *   manage their own VectorLayer lifecycle.
 * @param {(feature: import('ol/Feature').default) => import('ol/style/Style').Style} [opts.styleResolver] —
 *   style for any feature the caller added via onSketchUpdate that ISN'T one of the base's own
 *   role-tagged dots (identified by having a `_role` property) or the live-cursor dot.
 * @param {boolean} [opts.allowDblClickFinish=true]
 * @param {(collectedLength: number) => boolean} [opts.canFinish] — extra validity check beyond
 *   `collectedLength >= minCount` for Enter/double-click to actually finish (e.g. AREA26 also
 *   requires an EVEN count, not just >= 6). Auto-complete at `maxCount` always finishes
 *   regardless of this predicate — a finite cap is assumed to already be a valid count.
 * @param {() => void} [opts.onCleanup] — called once, whenever this collector tears itself down
 *   for ANY reason (finish, Escape-cancel, or external destroy()) — lets a caller stop its own
 *   extra resources (e.g. a startLivePreview() subscription) without reimplementing this
 *   module's finish/cancel/destroy bookkeeping.
 * @returns {{ getPlacedCount: () => number, getCollected: () => [number,number][],
 *   getPreviewLayer: () => import('ol/layer/Vector').default, destroy: () => void }}
 */
export function createMultiPointCollector(map, {
    minCount,
    maxCount = Infinity,
    onFinish,
    roleForIndex = () => 'anchor',
    onSketchUpdate = null,
    styleResolver = null,
    allowDblClickFinish = true,
    canFinish = (n) => n >= minCount,
    onCleanup = null,
}) {
    let collected = [];     // [[lng,lat], ...] in click order
    let collected3857 = []; // parallel EPSG:3857 array, for sketch rendering
    let done = false;       // true once finish()/cancel()/destroy() has run — ignore further events

    const previewSource = new VectorSource();
    const previewLayer = new VectorLayer({
        source: previewSource,
        zIndex: 10,
        style: (f) => {
            const role = f.get('_role');
            if (role === 'cursor') return PREVIEW_DOT_CURSOR;
            if (role) return getRoleDotStyle(role);
            return styleResolver ? styleResolver(f) : null;
        },
    });
    map.addLayer(previewLayer);

    function renderSketch(cursorCoord3857) {
        previewSource.clear(true);
        collected3857.forEach((c, i) => {
            const f = new Feature(new Point(c));
            f.set('_role', roleForIndex(i));
            previewSource.addFeature(f);
        });
        if (cursorCoord3857 && collected.length < maxCount) {
            const cf = new Feature(new Point(cursorCoord3857));
            cf.set('_role', 'cursor');
            previewSource.addFeature(cf);
        }
        onSketchUpdate?.({ previewSource, collected3857, cursorCoord3857: cursorCoord3857 ?? null });
    }

    function finish() {
        if (done) return;
        done = true;
        cleanup();
        onFinish([...collected]);
    }

    function cancel() {
        if (done) return;
        done = true;
        cleanup();
        // No onFinish call — user explicitly backed out.
    }

    function cleanup() {
        map.un('click', onClick);
        map.un('pointermove', onPointerMove);
        map.getViewport().removeEventListener('dblclick', onDblClick);
        document.removeEventListener('keydown', onKeyDown);
        map.removeLayer(previewLayer);
        onCleanup?.();
    }

    const onClick = (e) => {
        if (done || e.originalEvent.button !== 0) return;
        if (collected.length >= maxCount) return; // guard: ignore extra clicks past the cap
        const lonLat = toLonLat(e.coordinate);
        collected.push([lonLat[0], lonLat[1]]);
        collected3857.push(e.coordinate);
        renderSketch(null);
        if (collected.length >= maxCount) finish(); // fixed-count DrawRules: maxCount === minCount
    };

    const onPointerMove = (e) => {
        if (done || collected.length >= maxCount) return;
        renderSketch(e.coordinate);
    };

    const onDblClick = (e) => {
        if (done || !allowDblClickFinish) return;
        if (canFinish(collected.length)) { e.preventDefault(); finish(); }
    };

    const onKeyDown = (e) => {
        if (done) return;
        if (e.key === 'Enter' && canFinish(collected.length)) { finish(); return; }
        if (e.key === 'Escape') { cancel(); return; }
        if (e.key === 'Backspace' && collected.length > 0) {
            collected.pop();
            collected3857.pop();
            renderSketch(null);
        }
    };

    map.on('click', onClick);
    map.on('pointermove', onPointerMove);
    map.getViewport().addEventListener('dblclick', onDblClick);
    document.addEventListener('keydown', onKeyDown);

    return {
        interaction: null,
        getPlacedCount: () => collected.length,
        getCollected: () => [...collected],
        getPreviewLayer: () => previewLayer,
        destroy: cancel,
    };
}
